<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
    open: boolean;
    title: string;
    description: string;
    confirmLabel: string;
    isSubmitting: boolean;
}>();

const { t } = useI18n();
const emit = defineEmits<{
    close: [];
    confirm: [];
}>();

const close = () => {
    if (!props.isSubmitting) emit('close');
};

const confirm = () => {
    if (!props.isSubmitting) emit('confirm');
};
</script>

<template>
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="relative z-50" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-200"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-150"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75" />
            </TransitionChild>
            <div class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-200"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-150"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                            <DialogTitle class="text-lg font-semibold text-gray-800">
                                {{ title }}
                            </DialogTitle>
                            <p class="mt-2 text-sm text-gray-600">{{ description }}</p>
                            <div class="mt-6 flex flex-wrap justify-end gap-3">
                                <UiButton variant="white" text-variant="primary" @click="close">
                                    {{ t('whatsappMeta.confirmDialog.cancel') }}
                                </UiButton>
                                <UiButton
                                    variant="danger"
                                    text-variant="white"
                                    :disabled="isSubmitting"
                                    @click="confirm"
                                >
                                    {{ confirmLabel }}
                                </UiButton>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
